import { getBaseUrl } from './apiConfig';

/**
 * Extract the appropriate message from bilingual error messages
 * Format: "Arabic message / English message"
 * Returns the message in the current language
 */
export const extractLocalizedMessage = (message: string, currentLanguage: string): string => {
  // If message contains " / ", it's bilingual
  if (message.includes(' / ')) {
    const parts = message.split(' / ');
    if (parts.length === 2) {
      // Arabic is first part, English is second
      return currentLanguage === 'ar' ? parts[0].trim() : parts[1].trim();
    }
  }
  
  // If message contains " /" (with space before slash), handle it
  if (message.includes(' /')) {
    const parts = message.split(' /');
    if (parts.length === 2) {
      return currentLanguage === 'ar' ? parts[0].trim() : parts[1].trim();
    }
  }
  
  // Return original message if not bilingual
  return message.trim();
};

export const debounce = <A extends unknown[]>(
  func: (...args: A) => void,
  wait: number
): ((...args: A) => void) => {
  let timeout: ReturnType<typeof setTimeout> | null = null;

  return (...args: A) => {
    if (timeout) {
      clearTimeout(timeout);
    }
    timeout = setTimeout(() => {
      func(...args);
    }, wait);
  };
};

export const groupBy = <T, K extends keyof T>(array: T[], key: K): Map<T[K], T[]> => {
  const map = new Map<T[K], T[]>();
  
  for (const item of array) {
    const groupKey = item[key];
    const existing = map.get(groupKey) || [];
    existing.push(item);
    map.set(groupKey, existing);
  }
  
  return map;
};

export const getFileUrl = (fileUrl: string): string => {
  // If it's already a full URL with token, return it
  if (fileUrl.startsWith('http://') || fileUrl.startsWith('https://')) {
    // Check if token already exists
    if (fileUrl.includes('token=')) {
      return fileUrl;
    }
    // Add token to existing URL
    const token = localStorage.getItem('authToken');
    if (token) {
      const separator = fileUrl.includes('?') ? '&' : '?';
      return `${fileUrl}${separator}token=${encodeURIComponent(token)}`;
    }
    return fileUrl;
  }
  
  const baseUrl = getBaseUrl();
  const fullUrl = `${baseUrl}${fileUrl}`;
  
  // Add token as query parameter for authentication
  const token = localStorage.getItem('authToken');
  if (token) {
    // Check if URL already has query parameters
    const separator = fullUrl.includes('?') ? '&' : '?';
    // Check if token already exists in URL
    if (!fullUrl.includes('token=')) {
      return `${fullUrl}${separator}token=${encodeURIComponent(token)}`;
    }
  }
  
  return fullUrl;
};

/**
 * Get authenticated image URL with token (async version using blob)
 * This creates a blob URL that includes the Authorization header
 */
export const getAuthenticatedImageUrl = async (fileUrl: string): Promise<string> => {
  try {
    const baseUrl = getBaseUrl();
    const fullUrl = fileUrl.startsWith('http://') || fileUrl.startsWith('https://') 
      ? fileUrl 
      : `${baseUrl}${fileUrl}`;
    
    const token = localStorage.getItem('authToken');

    if (!token) {
      // If no token, return the URL with query parameter (will use token from query)
      return getFileUrl(fileUrl);
    }

    // Try to fetch with Authorization header first
    try {
      const response = await fetch(fullUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        // Convert response to blob
        const blob = await response.blob();
        // Create object URL from blob
        return URL.createObjectURL(blob);
      }
    } catch (error) {
      console.warn('Failed to fetch with Authorization header, falling back to query parameter:', error);
    }

    // Fallback: return URL with token in query parameter
    return getFileUrl(fileUrl);
  } catch (error) {
    console.error('Error loading authenticated image:', error);
    // Return original URL as fallback
    return getFileUrl(fileUrl);
  }
};
